<!--
  @component ProjectTile — a project at a glance, with presence.
  tier:      home primitive (richer than the list ProjectCard)
  purpose:   the visual unit of the Projects home — a gradient "cover" +
             letter-mark (no thumbnail needed), name + type, domain, status,
             last activity. Used by the carousel and the grid alike.
-->
<script lang="ts" module>
  import type { ProjectLike, ProjectStatus } from "./home-util.js";
  export type { ProjectLike, ProjectStatus };
</script>

<script lang="ts">
  import { projectGradient, initial, relTime, projectTypeLabel } from "./home-util.js";

  interface Props {
    project: ProjectLike;
    status?: ProjectStatus;
    lastActivityMs?: number;
    onClick?: () => void;
  }
  const { project, status = "idle", lastActivityMs, onClick }: Props = $props();

  const cover = $derived(projectGradient(project.id));
  const statusLabel: Record<ProjectStatus, string> = {
    live: "Live", building: "Building", error: "Issue", idle: "Idle",
  };

  function onKey(e: KeyboardEvent): void {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick?.(); }
  }
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex — with onClick the tile IS
     interactive (role=button + key handler); the article tag is structural. -->
<article
  class="tile"
  class:clickable={!!onClick}
  role={onClick ? "button" : undefined}
  tabindex={onClick ? 0 : undefined}
  onclick={() => onClick?.()}
  onkeydown={onClick ? onKey : undefined}
>
  <div class="cover" style:background={cover}>
    <span class="mark" aria-hidden="true">{initial(project.name)}</span>
    <span class="status status-{status}">
      <span class="led"></span>{statusLabel[status]}
    </span>
  </div>
  <div class="body">
    <div class="line">
      <h3>{project.name}</h3>
      <span class="type">{projectTypeLabel(project)}</span>
    </div>
    {#if project.productionDomain}
      <p class="domain"><code>{project.productionDomain}</code></p>
    {:else}
      <p class="domain"><span class="muted">no custom domain</span></p>
    {/if}
    <p class="meta">{status === "idle" ? "not deployed yet" : `active ${relTime(lastActivityMs)}`}</p>
  </div>
</article>

<style>
  .tile {
    display: flex;
    flex-direction: column;
    background: var(--glass-fill);
    border: 0.5px solid var(--border);
    border-radius: var(--radius-lg, 14px);
    overflow: hidden;
    font-family: var(--type-font);
    color: var(--text-primary);
    transition: transform var(--ease-fast), border-color var(--ease-fast), box-shadow var(--ease-fast);
  }
  .tile.clickable { cursor: pointer; }
  .tile.clickable:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    box-shadow: var(--shadow-lg);
  }
  .tile.clickable:focus-visible {
    outline: none;
    border-color: var(--border-focus);
    box-shadow: 0 0 0 2px var(--accent-glow);
  }

  .cover {
    position: relative;
    height: 92px;
    display: flex;
    align-items: flex-end;
    padding: var(--space-3);
  }
  .cover::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.28) 100%);
    pointer-events: none;
  }
  .mark {
    position: absolute;
    top: -6px;
    right: 8px;
    font-size: 76px;
    font-weight: 700;
    line-height: 1;
    color: rgba(255,255,255,0.14);
    letter-spacing: -0.04em;
    user-select: none;
  }
  .status {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.92);
    background: rgba(0,0,0,0.34);
    backdrop-filter: blur(6px);
    border-radius: var(--radius-pill);
    padding: 3px 9px 3px 7px;
  }
  .led { width: 7px; height: 7px; border-radius: var(--radius-pill); background: var(--text-tertiary); flex-shrink: 0; }
  .status-live .led { background: var(--success); box-shadow: 0 0 0 3px color-mix(in oklab, var(--success) 30%, transparent); }
  .status-building .led { background: var(--accent, #6ea8fe); animation: pulse 1.4s ease-in-out infinite; }
  .status-error .led { background: var(--error); }
  .status-idle .led { background: var(--text-tertiary); }
  @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }

  .body { display: flex; flex-direction: column; gap: 5px; padding: var(--space-3) var(--space-4) var(--space-4); }
  .line { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); }
  h3 {
    margin: 0; font-size: 14.5px; font-weight: 600; letter-spacing: -0.01em;
    color: var(--text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  .type {
    flex-shrink: 0; font-size: 10px; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase;
    color: var(--text-tertiary); background: var(--bg-active); border: 0.5px solid var(--border-subtle);
    border-radius: var(--radius-sm); padding: 2px 6px;
  }
  .domain { margin: 0; font-size: 12px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .domain code { font-family: var(--type-mono); font-size: 11px; color: var(--accent-text); }
  .muted { color: var(--text-tertiary); font-style: italic; }
  .meta { margin: 0; font-size: 11px; color: var(--text-tertiary); }
</style>
